"use client"

import { useState, useEffect, useRef } from "react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts"
import { MapContainer, TileLayer, Popup, Marker, useMap } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import { Activity, ThumbsUp, Hash, Search, MapPin } from "lucide-react"
import "./dashboardPage.css"
import axios from "axios"
import { Icon } from "leaflet"
import MarkerClusterGroup from "react-leaflet-cluster"
import { useLocation } from "react-router-dom"
import "leaflet/dist/leaflet.css"
import "leaflet-routing-machine/dist/leaflet-routing-machine.css"
import "leaflet-control-geocoder/dist/Control.Geocoder.css"
import L from "leaflet"
import WordCloud from "./wordCloud.tsx"
import Trends from "./trendingDiagram.tsx"

// Import these outside of the component
import "leaflet-routing-machine"
import "leaflet-control-geocoder"

const customIcon = new Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/128/3128/3128310.png",
  iconSize: [38, 38], // size of the icon
})

// Patch Leaflet Routing Machine to prevent removeLayer errors
const patchLeafletRoutingMachine = () => {
  if (L.Routing && L.Routing.Plan && L.Routing.Plan.prototype) {
    const originalRemoveWaypoint = L.Routing.Plan.prototype.removeWaypoint

    L.Routing.Plan.prototype.removeWaypoint = function (waypoint, index) {
      try {
        return originalRemoveWaypoint.apply(this, arguments)
      } catch (e) {
        console.warn("Suppressed error in removeWaypoint:", e)
        return this
      }
    }
  }

  if (L.Routing && L.Routing.Line && L.Routing.Line.prototype) {
    const originalOnRemove = L.Routing.Line.prototype.onRemove

    L.Routing.Line.prototype.onRemove = function (map) {
      try {
        if (this._line && this._line._layers) {
          return originalOnRemove.apply(this, arguments)
        }
      } catch (e) {
        console.warn("Suppressed error in Line.onRemove:", e)
      }
      return this
    }

    const originalClearLines = L.Routing.Line.prototype._clearLines

    L.Routing.Line.prototype._clearLines = function () {
      try {
        if (this._line && this._map) {
          return originalClearLines.apply(this, arguments)
        }
      } catch (e) {
        console.warn("Suppressed error in _clearLines:", e)
      }
      return this
    }
  }
}

// Apply the patch immediately
patchLeafletRoutingMachine()

// Routing control component that needs to access the map instance
function RoutingMachine({ waypoints, setRouteInfo }) {
  const map = useMap()
  const routingControlRef = useRef(null)
  const isMountedRef = useRef(true)

  // Clean up function to safely remove routing control
  const cleanupRoutingControl = () => {
    if (routingControlRef.current && map) {
      try {
        // First, clear all waypoints to prevent any routing calculations
        if (routingControlRef.current.getPlan && routingControlRef.current.getPlan()) {
          const plan = routingControlRef.current.getPlan()
          if (plan && plan.setWaypoints) {
            plan.setWaypoints([])
          }
        }

        // Then remove the control from the map
        map.removeControl(routingControlRef.current)
      } catch (error) {
        console.warn("Error during routing control cleanup:", error)
      }
      routingControlRef.current = null
    }
  }

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      cleanupRoutingControl()
    }
  }, [])

  useEffect(() => {
    if (!map || !isMountedRef.current || waypoints.length < 2) return

    // Clean up previous routing control
    cleanupRoutingControl()

    // Add a small delay before creating a new routing control
    // This helps ensure the map is in a stable state
    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current || !map) return

      try {
        // Create new routing control with error handling
        const routingControl = L.Routing.control({
          waypoints: waypoints.map((wp) => L.latLng(wp.lat, wp.lng)),
          routeWhileDragging: false, // Set to false to reduce errors
          showAlternatives: true,
          fitSelectedRoutes: true,
          lineOptions: {
            styles: [{ color: "#E91E63", weight: 4 }],
            extendToWaypoints: true,
            missingRouteTolerance: 0,
          },
          createMarker: (i, wp) =>
            L.marker(wp.latLng, {
              icon: new L.Icon({
                iconUrl:
                  i === 0
                    ? "https://cdn-icons-png.flaticon.com/512/684/684908.png"
                    : "https://cdn-icons-png.flaticon.com/512/684/684809.png",
                iconSize: [38, 38],
                iconAnchor: [19, 38],
              }),
            }),
          // Use a custom router to handle errors
          router: L.Routing.osrmv1({
            serviceUrl: "https://router.project-osrm.org/route/v1",
            timeout: 10000, // Increase timeout
            suppressDemoServerWarning: true,
          }),
        })

        // Only add to map if component is still mounted
        if (isMountedRef.current && map) {
          routingControl.addTo(map)

          // Update route info when route is calculated
          routingControl.on("routesfound", (e) => {
            if (!isMountedRef.current) return

            try {
              const routes = e.routes
              if (routes && routes.length > 0) {
                const summary = routes[0].summary

                // Calculate hours and minutes for more accurate display
                const totalHours = Math.floor(summary.totalTime / 3600)
                const totalMinutes = Math.floor((summary.totalTime % 3600) / 60)
                const totalSeconds = Math.floor(summary.totalTime % 60)

                setRouteInfo({
                  distance: (summary.totalDistance / 1000).toFixed(2), // km
                  duration: {
                    hours: totalHours,
                    minutes: totalMinutes,
                    seconds: totalSeconds,
                    totalSeconds: summary.totalTime,
                  },
                })
              }
            } catch (error) {
              console.error("Error processing route data:", error)
            }
          })

          routingControlRef.current = routingControl
        }
      } catch (error) {
        console.error("Error creating routing control:", error)
      }
    }, 100) // Small delay to ensure map is ready

    return () => {
      clearTimeout(timeoutId)
    }
  }, [map, waypoints, setRouteInfo])

  return null
}

// Geocoding function to convert place names to coordinates
async function geocodeLocation(locationName) {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationName)}`,
    )
    const data = await response.json()

    if (data && data.length > 0) {
      return {
        lat: Number.parseFloat(data[0].lat),
        lng: Number.parseFloat(data[0].lon),
        display_name: data[0].display_name,
      }
    }
    return null
  } catch (error) {
    console.error("Geocoding error:", error)
    return null
  }
}

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const Dashboard = () => {
  const query = useQuery()
  const keyword = query.get("keyword") || ""
  const [isProcessing, setIsProcessing] = useState(true)
  const [activeTab, setActiveTab] = useState("tweets")
  const [tweetMap, setTweetMap] = useState([])
  const [showTweetTrend, setShowTweetTrend] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState("")
  const [sentimentScore, setSentimentScore] = useState(0)
  const [sentimentPeriod, setSentimentPeriod] = useState("24h")
  const [sentimentStartTime, setSentimentStartTime] = useState("")
  const [sentimentEndTime, setSentimentEndTime] = useState("")
  const [hashtags, setHashtags] = useState([])
  const [aggregation, setAggregation] = useState("daily")
  const [trendData, setTrendData] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // Routing state
  const [startPoint, setStartPoint] = useState("")
  const [endPoint, setEndPoint] = useState("")
  const [waypoints, setWaypoints] = useState([])
  const [routeInfo, setRouteInfo] = useState({
    distance: "0",
    duration: { hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 },
  })
  const [isCalculating, setIsCalculating] = useState(false)
  const [routeError, setRouteError] = useState("")
  const [mapCenter, setMapCenter] = useState([31.9, 35.2]) // Center on Palestine region
  const [mapZoom, setMapZoom] = useState(9)
  const [mapKey, setMapKey] = useState(Date.now()) // Used to force map re-render

  const [stats, setStats] = useState({
    total_tweets: 0,
    total_locations: 0,
    positive_sentiment_percentage: 0,
    total_topics: 0,
  })

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BEURL}/stats`)
      .then((response) => {
        setStats(response.data)
      })
      .catch((error) => {
        console.error("Error fetching stats:", error)
      })
    console.log(process.env.REACT_APP_BEURL, "AMRO")
  }, [])

  const handleSentimentAnalysis = async () => {
    if (!sentimentStartTime || !sentimentEndTime) {
      alert("Please select both start and end times.")
      return
    }

    const apiUrl = `${process.env.REACT_APP_BEURL}/sentiment/custom-range`
    const params = {
      start_time: sentimentStartTime,
      end_time: sentimentEndTime,
      keyword: keyword,
    }

    try {
      const response = await axios.get(apiUrl, { params })
      const data = response.data
      setSentimentScore(data.average_sentiment)
    } catch (error) {
      console.error("Error fetching sentiment data:", error)
      alert("An error occurred while analyzing sentiment.")
    }
  }

  useEffect(() => {
    const fetchTrendData = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get(`${process.env.REACT_APP_BEURL}/tweets/temporal_distribution`, {
          params: { keyword, aggregation },
        })
        setTrendData(
          response.data.map((item) => ({
            timestamp: item.key,
            count: item.doc_count,
          })),
        )
      } catch (error) {
        console.error("Error fetching trend data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchTrendData()
  }, [keyword, aggregation])

  const fetchTweets = async (keyword) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BEURL}/tweets?keyword=${keyword}`)
      const tweets = response.data

      const tweetMap = tweets.map((tweet) => {
        return {
          id: tweet._id,
          lat: tweet._source.coordinates.coordinates
            ? tweet._source.coordinates.coordinates[1]
            : tweet._source.coordinates[1],
          lng: tweet._source.coordinates.coordinates
            ? tweet._source.coordinates.coordinates[0]
            : tweet._source.coordinates[0],
          text: tweet._source.text,
          sentiment: tweet._source.final_sentiment,
        }
      })

      setTweetMap(tweetMap)
    } catch (error) {
      console.error("Error fetching tweets:", error)
    }
  }

  const fetchHashtags = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BEURL}/hashtags`)
      setHashtags(response.data.hashtags)
    } catch (error) {
      console.error("Error fetching tweets:", error)
    }
  }

  useEffect(() => {
    fetchHashtags()
  }, [])

  useEffect(() => {
    fetchTweets(keyword)
  }, [keyword])

  useEffect(() => {
    const timer = setInterval(() => {
      setIsProcessing((prev) => !prev)
    }, 5000)

    return () => clearInterval(timer)
  }, [])

  const getSentimentEmoji = (score) => {
    if (score > 0.7) return "😃";
    if (score > 0.2 && score <= 0.7) return "🙂";
    if (score > -0.2 && score <= 0.2) return "😐";
    if (score > -0.7 && score <= -0.2) return "😔";
    if (score <= -0.7) return "😞";
  };


  // Calculate route function
  const calculateRoute = async () => {
    if (!startPoint || !endPoint) {
      setRouteError("Please enter both starting point and destination")
      return
    }

    setIsCalculating(true)
    setRouteError("")

    // Clear existing waypoints first to prevent routing errors
    setWaypoints([])

    // Force map to re-render with current settings
    // This will unmount and remount the map, cleaning up any existing routing controls
    setMapKey(Date.now())

    try {
      // Geocode both locations
      const startLocation = await geocodeLocation(startPoint)
      const endLocation = await geocodeLocation(endPoint)

      if (!startLocation) {
        setRouteError(`Could not find location: ${startPoint}`)
        setIsCalculating(false)
        return
      }

      if (!endLocation) {
        setRouteError(`Could not find location: ${endPoint}`)
        setIsCalculating(false)
        return
      }

      // Calculate the center point between the two locations
      const centerLat = (startLocation.lat + endLocation.lat) / 2
      const centerLng = (startLocation.lng + endLocation.lng) / 2

      // Update map center and zoom
      setMapCenter([centerLat, centerLng])

      // Adjust zoom level based on distance
      const distance = calculateDistance(startLocation.lat, startLocation.lng, endLocation.lat, endLocation.lng)

      if (distance > 100) setMapZoom(7)
      else if (distance > 50) setMapZoom(8)
      else if (distance > 20) setMapZoom(9)
      else if (distance > 10) setMapZoom(10)
      else setMapZoom(11)

      // Force map to re-render with new center and zoom
      const newMapKey = Date.now()
      setMapKey(newMapKey)

      // Add a small delay before setting waypoints to ensure the map is ready
      setTimeout(() => {
        setWaypoints([startLocation, endLocation])
      }, 500)
    } catch (error) {
      console.error("Error calculating route:", error)
      setRouteError("Error calculating route. Please try again.")
    } finally {
      setIsCalculating(false)
    }
  }

  // Haversine formula to calculate distance between two points
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371 // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1)
    const dLon = deg2rad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c // Distance in km
    return distance
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180)
  }

  // Format duration for display
  const formatDuration = (duration) => {
    let result = ""
    if (duration.hours > 0) {
      result += `${duration.hours} hour${duration.hours !== 1 ? "s" : ""} `
    }
    if (duration.minutes > 0 || duration.hours > 0) {
      result += `${duration.minutes} minute${duration.minutes !== 1 ? "s" : ""} `
    }
    result += `${duration.seconds} second${duration.seconds !== 1 ? "s" : ""}`
    return result
  }

  if (keyword) {
    const formatXAxis = (tickItem) => {
      if (aggregation === "daily") {
        const dayName = new Date(tickItem).toLocaleDateString("en-US", { weekday: "long" })
        return dayName || ""
      } else {
        return new Date(tickItem).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      }
    }

    return (
      <div className="container mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold mb-8 gradient-text text-center">Real-time Tweet Analysis Dashboard</h1>

        <div className="mb-8 flex items-center justify-center">
          <div
            className={`w-4 h-4 rounded-full mr-2 ${isProcessing ? "bg-green-500" : "bg-red-500"} animate-pulse`}
          ></div>
          <span className="text-gray-600 font-semibold">
            {isProcessing ? "Processing tweets in real-time" : "System idle"}
          </span>
        </div>

        <form action="/dashboard" className="mb-2 max-w-2xl mx-auto">
          <div className="flex items-center border-2 border-indigo-500 rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
            <input
              type="text"
              name="keyword"
              placeholder="Enter a keyword to analyze tweets..."
              className="flex-grow px-6 py-4 text-lg focus:outline-none"
            />
            <button
              type="submit"
              className="bg-indigo-500 text-white px-6 py-4 hover:bg-indigo-600 transition-colors duration-300"
            >
              <Search className="w-6 h-6" />
              <span className="sr-only">Search</span>
            </button>
          </div>
        </form>

        <div className="mb-8">
          {/* Route Calculator */}
          <div className="card p-6 mb-8 bg-white rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4 gradient-text">Route Calculator</h2>

            {/* Route Input Form */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
              <div>
                <label htmlFor="startPoint" className="block text-sm font-medium text-gray-700 mb-1">
                  Starting Point
                </label>
                <input
                  type="text"
                  id="startPoint"
                  value={startPoint}
                  onChange={(e) => setStartPoint(e.target.value)}
                  placeholder="Enter starting location"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="destination" className="block text-sm font-medium text-gray-700 mb-1">
                  Destination
                </label>
                <input
                  type="text"
                  id="destination"
                  value={endPoint}
                  onChange={(e) => setEndPoint(e.target.value)}
                  placeholder="Enter destination"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <button
                  onClick={calculateRoute}
                  disabled={isCalculating}
                  className="w-full px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 transition-colors duration-300 disabled:bg-pink-300"
                >
                  {isCalculating ? "Calculating..." : "Calculate Route"}
                </button>
              </div>
            </div>

            {/* Error message */}
            {routeError && <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">{routeError}</div>}

            {/* Route Information */}
            {routeInfo.distance !== "0" && (
              <div className="mb-4 p-4 bg-gray-100 rounded-md">
                <h3 className="font-semibold text-lg mb-2">Route Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Distance:</span> {routeInfo.distance} km
                  </div>
                  <div>
                    <span className="font-medium">Duration:</span> {formatDuration(routeInfo.duration)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="card p-6 mb-8 mt-4">
            <h2 className="text-xl font-semibold mb-4 gradient-text">Sentiment by Location</h2>
            <div style={{ height: "700px" }}>
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: "100vh", width: "100%", zIndex: 0 }}
                key={mapKey} // Force re-render when center/zoom changes
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Routing Machine */}
                {waypoints.length >= 2 && <RoutingMachine waypoints={waypoints} setRouteInfo={setRouteInfo} />}

                <MarkerClusterGroup>
                  {tweetMap.map((tweet) => (
                    <Marker key={tweet.id} position={[tweet.lat, tweet.lng]} icon={customIcon}>
                      <Popup>{tweet.text}</Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>
          </div>
          <Trends keyword={keyword} setKeyword={setSearchKeyword} />
        </div>
        <div className="card p-6 mb-8 bg-white rounded-xl shadow-2xl">
          <h2 className="text-2xl font-semibold mb-4 gradient-text">😊 Sentiment Analysis</h2>
          <div className="flex flex-col items-center justify-center">
            <div className="mb-4 flex space-x-4">
              <div>
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="datetime-local"
                  id="startTime"
                  value={sentimentStartTime}
                  onChange={(e) => setSentimentStartTime(e.target.value)}
                  className="border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm"
                />
              </div>
              <div>
                <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="datetime-local"
                  id="endTime"
                  value={sentimentEndTime}
                  onChange={(e) => setSentimentEndTime(e.target.value)}
                  className="border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm"
                />
              </div>
            </div>
            <button
              onClick={handleSentimentAnalysis}
              className="mb-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-300"
            >
              Analyze Sentiment
            </button>
            <div className="relative w-64 h-64">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  className={`${sentimentScore === -1 ? "text-red-500" : "text-gray-200"} stroke-current`}
                  strokeWidth="10"
                  cx="50"
                  cy="50"
                  r="40"
                  fill="transparent"
                />
                <circle
                  className={`progress-ring__circle stroke-current ${sentimentScore > 0.7 ? "text-green-500" : sentimentScore > 0.2 ? "text-green-400" : sentimentScore > -0.2 ? "text-yellow-500" : sentimentScore > -0.7 ? "text-red-400" : "text-red-500"}`}
                  strokeWidth="10"
                  strokeLinecap="round"
                  cx="50"
                  cy="50"
                  r="40"
                  fill="transparent"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - (sentimentScore + 1) / 2)}`}
                  transform="rotate(-90 50 50)"
                ></circle>
                <text x="50" y="45" fontFamily="Verdana" fontSize="20" textAnchor="middle" alignmentBaseline="middle">
                  {sentimentScore.toFixed(2)}
                </text>
                <text x="50" y="65" fontFamily="Verdana" fontSize="24" textAnchor="middle" alignmentBaseline="middle">
                  {getSentimentEmoji(sentimentScore)}
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div className="container mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold mb-8 gradient-text text-center">Real-time Tweet Analysis Dashboard</h1>

        <div className="mb-8 flex items-center justify-center">
          <div
            className={`w-4 h-4 rounded-full mr-2 ${isProcessing ? "bg-green-500" : "bg-red-500"} animate-pulse`}
          ></div>
          <span className="text-gray-600 font-semibold">
            {isProcessing ? "Processing tweets in real-time" : "System idle"}
          </span>
        </div>
        <form action="/dashboard" className="mb-2 max-w-2xl mx-auto">
          <div className="flex items-center border-2 border-indigo-500 rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
            <input
              type="text"
              name="keyword"
              placeholder="Enter a keyword to analyze tweets..."
              className="flex-grow px-6 py-4 text-lg focus:outline-none"
            />
            <button
              type="submit"
              className="bg-indigo-500 text-white px-6 py-4 hover:bg-indigo-600 transition-colors duration-300"
            >
              <Search className="w-6 h-6" />
              <span className="sr-only">Search</span>
            </button>
          </div>
        </form>

        <div className="mb-8">
          {/* Route Calculator */}
          <div className="card p-6 mb-8 bg-white rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4 gradient-text">Route Calculator</h2>

            {/* Route Input Form */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
              <div>
                <label htmlFor="startPoint" className="block text-sm font-medium text-gray-700 mb-1">
                  Starting Point
                </label>
                <input
                  type="text"
                  id="startPoint"
                  value={startPoint}
                  onChange={(e) => setStartPoint(e.target.value)}
                  placeholder="Enter starting location"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="destination" className="block text-sm font-medium text-gray-700 mb-1">
                  Destination
                </label>
                <input
                  type="text"
                  id="destination"
                  value={endPoint}
                  onChange={(e) => setEndPoint(e.target.value)}
                  placeholder="Enter destination"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <button
                  onClick={calculateRoute}
                  disabled={isCalculating}
                  className="w-full px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 transition-colors duration-300 disabled:bg-pink-300"
                >
                  {isCalculating ? "Calculating..." : "Calculate Route"}
                </button>
              </div>
            </div>

            {/* Error message */}
            {routeError && <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">{routeError}</div>}

            {/* Route Information */}
            {routeInfo.distance !== "0" && (
              <div className="mb-4 p-4 bg-gray-100 rounded-md">
                <h3 className="font-semibold text-lg mb-2">Route Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Distance:</span> {routeInfo.distance} km
                  </div>
                  <div>
                    <span className="font-medium">Duration:</span> {formatDuration(routeInfo.duration)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-center space-x-4">
            <button
              className={`px-4 py-2 rounded-full ${activeTab === "tweets" ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-700"} transition-colors duration-300`}
              onClick={() => {
                setActiveTab("tweets")
                setShowTweetTrend(null)
              }}
            >
              Latest Tweets
            </button>
            <button
              className={`px-4 py-2 rounded-full ${activeTab === "trends" ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-700"} transition-colors duration-300`}
              onClick={() => {
                setActiveTab("trends")
                setShowTweetTrend(null)
              }}
            >
              Trending Hashtags
            </button>
          </div>
          <div className="mt-8">
            {activeTab === "tweets" && (
              <div className="card p-6 animate-fadeIn">
                <h2 className="text-xl font-semibold mb-4 gradient-text">Latest Tweets</h2>
                <div className="space-y-4">
                  {tweetMap.slice(0, 5).map((tweet) => (
                    <div
                      key={tweet.id}
                      className="border-b pb-2 hover:bg-gray-50 transition-colors duration-300 p-2 rounded"
                    >
                      <p className="text-gray-700">{tweet.text}</p>
                      <span
                        className={`text-sm font-semibold ${tweet.sentiment == "positive"
                          ? "text-green-600"
                          : tweet.sentiment == "negative"
                            ? "text-red-600"
                            : "text-yellow-600"
                          }`}
                      >
                        {tweet.sentiment || "natural"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "trends" && (
              <div className="card p-6 animate-fadeIn">
                <h2 className="text-xl font-semibold mb-4 gradient-text">Trending Hashtags</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={hashtags}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hashtag" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="url(#colorGradient)" />
                    <defs>
                      <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.8} />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        <div className="card p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 gradient-text">Sentiment by Location</h2>
          <div style={{ height: "700px" }}>
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: "100vh", width: "100%", zIndex: 0 }}
              key={mapKey}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Routing Machine */}
              {waypoints.length >= 2 && <RoutingMachine waypoints={waypoints} setRouteInfo={setRouteInfo} />}

              <MarkerClusterGroup>
                {tweetMap.map((tweet) => (
                  <Marker key={tweet.id} position={[tweet.lat, tweet.lng]} icon={customIcon}>
                    <Popup>{tweet.text}</Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        </div>
        <div className="card p-6 mb-8 bg-white rounded-xl shadow-2xl">
          <h2 className="text-2xl font-semibold mb-4 gradient-text">🔥 Trending Topics</h2>
          <WordCloud words={hashtags} />
        </div>
        <div className="grid md:grid-cols-4 gap-8">
          {[
            {
              title: "Total Tweets",
              value: stats.total_tweets,
              icon: <Activity className="w-8 h-8 text-indigo-600" />,
            },
            {
              title: "Total Locations",
              value: stats.total_locations,
              icon: <MapPin className="w-8 h-8 text-red-500" />,
            },
            {
              title: "Positive Sentiment",
              value: `${stats.positive_sentiment_percentage.toFixed(1)}%`,
              icon: <ThumbsUp className="w-8 h-8 text-green-500" />,
            },
            { title: "Total Topics", value: stats.total_topics, icon: <Hash className="w-8 h-8 text-blue-500" /> },
          ].map((stat, index) => (
            <div
              key={index}
              className="card p-6 text-center group hover:bg-indigo-50 transition-all duration-300 transform hover:scale-105"
            >
              <div className="flex justify-center mb-4">{stat.icon}</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-800">{stat.title}</h3>
              <p className="text-3xl font-bold gradient-text">{stat.value}</p>
            </div>
          ))}
        </div>
      </div>
    )
  }
}

export default Dashboard
